export type TeamManagementTeam = {
  id: number;
  name: string;
};

export type TeamManagementMember = {
  id: number;
  email: string;
  status: "active" | "invited" | "deactivated";
  invitation_pending: boolean;
  terms_accepted: boolean;
  terms_accepted_at: string | null;
  can_manage_users: boolean;
  discarded: boolean;
};

async function parseJson<T>(response: Response): Promise<T> {
  const payload = await response.json();
  if (!response.ok) {
    throw new Error(payload.message || payload.error || response.statusText);
  }
  return payload as T;
}

export async function fetchManageableTeams(options?: {
  teamManagementOnly?: boolean;
}): Promise<TeamManagementTeam[]> {
  const params = new URLSearchParams();
  if (options?.teamManagementOnly) params.set("team_management", "1");
  const query = params.toString();
  const response = await fetch(`/api/v1/team_management/teams${query ? `?${query}` : ""}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const data = await parseJson<{ teams: TeamManagementTeam[] }>(response);
  return data.teams;
}

export async function fetchTeamMembers(teamId: number): Promise<TeamManagementMember[]> {
  const response = await fetch(`/api/v1/team_management/teams/${teamId}/members`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const data = await parseJson<{ members: TeamManagementMember[] }>(response);
  return data.members;
}

export async function inviteTeamMember(teamId: number, email: string): Promise<{ message: string }> {
  const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content") ?? "";
  const response = await fetch(`/api/v1/team_management/teams/${teamId}/members`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
    },
    body: JSON.stringify({ member: { email } }),
  });
  return parseJson<{ message: string }>(response);
}

export async function deactivateTeamMember(teamId: number, userId: number): Promise<void> {
  await postMemberAction(teamId, userId, "deactivate");
}

export async function activateTeamMember(teamId: number, userId: number): Promise<void> {
  await postMemberAction(teamId, userId, "activate");
}

export async function sendTeamMemberPasswordReset(teamId: number, userId: number): Promise<void> {
  await postMemberAction(teamId, userId, "send_password_reset");
}

export async function resendTeamMemberInvitation(teamId: number, userId: number): Promise<void> {
  await postMemberAction(teamId, userId, "resend_invitation");
}

async function postMemberAction(teamId: number, userId: number, action: string): Promise<void> {
  const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content") ?? "";
  const response = await fetch(
    `/api/v1/team_management/teams/${teamId}/members/${userId}/${action}`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrfToken,
      },
    },
  );
  await parseJson(response);
}
